import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { hrAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, Upload, Trash2, FileText, Save, User, AlertTriangle, Users2, Plus
} from 'lucide-react';
import { groupSkills } from './skillGrouping';
import { SearchableSelect } from '@/components/SearchableSelect';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LEVELS = ['S1', 'S2', 'S3', 'S4'];

export default function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.app_roles?.hr_training === 'manager';

  const [emp, setEmp] = useState(null);
  const [allEmps, setAllEmps] = useState([]);
  const [fps, setFps] = useState([]);
  const [quals, setQuals] = useState([]);
  const [skills, setSkills] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [elig, setElig] = useState({});
  const [eligEmp, setEligEmp] = useState(null);
  const [levelEdits, setLevelEdits] = useState({});
  const [extraSkillIds, setExtraSkillIds] = useState([]);
  const [addGroup, setAddGroup] = useState('');
  const [addSkill, setAddSkill] = useState('');
  const [exp, setExp] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [photoTs, setPhotoTs] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(); const attRef = useRef(); const [attCat, setAttCat] = useState('qualification');

  const load = async () => {
    try {
      const [e, all, f, q, s, g, el] = await Promise.all([
        hrAPI.getEmployee(id), hrAPI.listEmployees(), hrAPI.listFunctionalPositions(),
        hrAPI.listMaster('qualifications'), hrAPI.listSkills(), hrAPI.skillGaps(id),
        hrAPI.employeeEligibility(id),
      ]);
      setEmp(e.data); setAllEmps(all.data || []); setFps(f.data || []);
      setQuals(q.data || []); setSkills(s.data || []); setGaps(g.data.gaps || []);
      setElig(el.data.positions || {}); setEligEmp(el.data.employee || null);
      setExp(e.data.experience_years ?? '');
      setSupervisorId(e.data.supervisor_id || '');
      const em = {}; (e.data.skill_levels || []).forEach(sl => { em[sl.skill_id] = sl.level; });
      // auto-add skills passed from Pending Skill Updates (?add_skills=id1,id2)
      const addParam = searchParams.get('add_skills');
      const preIds = addParam ? addParam.split(',').filter(Boolean) : [];
      setLevelEdits(em); setExtraSkillIds(preIds); setAddGroup(''); setAddSkill('');
      if (preIds.length) {
        const rated = new Set(Object.keys(em));
        const toAdd = preIds.filter(x => !rated.has(x)).length;
        toast.success(`${preIds.length} skill(s) from the training loaded below${toAdd ? '' : ' (already rated)'} — set the level(s) and Save.`);
        setSearchParams({}, { replace: true });
      }
    } catch { toast.error('Failed to load employee'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!emp) return <div className="text-sm text-slate-400 py-10 text-center">Loading...</div>;

  const directReports = allEmps.filter(e => e.supervisor_id === id);

  // Required skills across the employee's assigned functional positions (highest level wins)
  const reqMap = {};
  (emp.functional_position_ids || []).forEach(fpId => {
    const fp = fps.find(f => f.id === fpId);
    (fp?.desired_skills || []).forEach(ds => {
      const cur = reqMap[ds.skill_id];
      if (!cur || LEVELS.indexOf(ds.min_level) > LEVELS.indexOf(cur)) reqMap[ds.skill_id] = ds.min_level;
    });
  });
  // Relevant = required by a functional position OR the employee has a rating OR manually added
  const relevantIds = new Set([
    ...Object.keys(reqMap),
    ...Object.entries(levelEdits).filter(([, v]) => v).map(([k]) => k),
    ...extraSkillIds,
  ]);
  const relevantGroups = groupSkills(skills.filter(s => relevantIds.has(s.id)));
  const addableSkills = skills.filter(s => (addGroup ? s.skill_group_id === addGroup : true) && !relevantIds.has(s.id));

  const addRelevantSkill = () => {
    if (!addSkill) { toast.error('Select a skill to add'); return; }
    setExtraSkillIds(prev => [...prev, addSkill]); setAddSkill('');
  };

  const setGroupLevel = (grpSkills, level) =>
    setLevelEdits(prev => { const n = { ...prev }; grpSkills.forEach(s => { n[s.id] = level; }); return n; });

  const saveProfile = async () => {
    setSaving(true);
    try {
      await hrAPI.updateProfile(id, {
        experience_years: exp === '' ? 0 : parseFloat(exp),
        supervisor_id: supervisorId || '',
        functional_position_ids: emp.functional_position_ids || [],
        qualification_ids: emp.qualification_ids || [],
      });
      toast.success('Profile saved'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const toggleId = (field, val) => {
    setEmp(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };

  const toggleFp = (fp) => {
    const active = (emp.functional_position_ids || []).includes(fp.id);
    const r = elig[fp.id];
    if (!active && r && !r.eligible && !r.no_requirement) {
      toast.warning(`${emp.name} does not currently meet eligibility for "${fp.name}". You can still assign it.`);
    }
    toggleId('functional_position_ids', fp.id);
  };

  const reasonText = (r) => {
    if (!r) return '';
    if (r.no_requirement) return 'No qualification requirement';
    if (r.eligible && r.matched) return `Meets ${r.matched.qualification_name}${r.matched.min_experience_years ? ` + ${r.matched.min_experience_years}yr` : ''}`;
    const alts = (r.alternatives || []).map(a => `${a.qualification_name}${a.min_experience_years ? ` +${a.min_experience_years}yr` : ''}`).join(' OR ');
    return `Needs ${alts || 'requirement'}`;
  };

  const saveLevels = async () => {
    const entries = Object.entries(levelEdits).filter(([, lv]) => lv).map(([skill_id, level]) => ({ skill_id, level }));
    if (entries.length === 0) { toast.error('Set at least one skill level'); return; }
    setSaving(true);
    try { await hrAPI.setSkillLevels(id, entries); toast.success('Skill levels updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const fd = new FormData(); fd.append('file', file); await hrAPI.uploadPhoto(id, fd); toast.success('Photo updated'); setPhotoTs(Date.now()); setEmp(p => ({ ...p, has_photo: true })); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { if (photoRef.current) photoRef.current.value = ''; }
  };

  const onAttachment = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const fd = new FormData(); fd.append('file', file); await hrAPI.uploadAttachment(id, attCat, fd); toast.success('Attachment uploaded'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { if (attRef.current) attRef.current.value = ''; }
  };

  const delAttachment = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try { await hrAPI.deleteAttachment(id, attId); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="hr-employee-detail-page">
      <button onClick={() => navigate('/hr')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="hr-emp-back">
        <ArrowLeft className="w-4 h-4" /> Back to Employees
      </button>

      {/* Header */}
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-5 flex items-center gap-5">
          <div className="relative">
            <div className="w-24 h-24 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center border">
              {emp.has_photo
                ? <img src={`${BACKEND}/api/hr/employees/${id}/photo?t=${photoTs}`} alt={emp.name} className="w-full h-full object-cover" data-testid="hr-emp-photo" />
                : <User className="w-10 h-10 text-slate-300" />}
            </div>
            {canManage && (
              <>
                <input type="file" accept="image/*" ref={photoRef} onChange={onPhoto} className="hidden" data-testid="hr-emp-photo-input" />
                <button onClick={() => photoRef.current?.click()} className="absolute -bottom-2 -right-2 bg-pink-600 text-white rounded-full p-1.5 shadow" data-testid="hr-emp-photo-btn"><Camera className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-[#0F172A]">{emp.name}</h1>
            <div className="flex flex-wrap gap-2 mt-1 text-sm text-slate-500">
              {emp.emp_code && <span className="font-mono">{emp.emp_code}</span>}
              {emp.email && <span>· {emp.email}</span>}
              <Badge variant="outline" className={`text-[10px] ${emp.is_user ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600'}`}>{emp.is_user ? 'Login User' : 'Non-user'}</Badge>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(emp.functional_positions || []).map(fp => <Badge key={fp.id} variant="outline" className="text-[10px] bg-pink-50 text-pink-700 border-pink-200">{fp.name}</Badge>)}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Profile */}
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-base">Profile</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Work Experience (yrs)</Label>
                <Input type="number" value={exp} onChange={e => setExp(e.target.value)} disabled={!canManage} data-testid="hr-emp-exp-input" />
              </div>
              <div>
                <Label className="text-xs">Immediate Supervisor</Label>
                <Select value={supervisorId || '__none__'} onValueChange={v => setSupervisorId(v === '__none__' ? '' : v)} disabled={!canManage}>
                  <SelectTrigger data-testid="hr-emp-supervisor-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {allEmps.filter(e => e.id !== id).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Qualifications</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {quals.map(q => {
                  const active = (emp.qualification_ids || []).includes(q.id);
                  return <button key={q.id} disabled={!canManage} onClick={() => toggleId('qualification_ids', q.id)}
                    className={`text-[11px] px-2 py-1 rounded border ${active ? 'bg-pink-50 text-pink-700 border-pink-300' : 'bg-white text-slate-500 border-slate-200'}`}
                    data-testid={`hr-emp-qual-${q.id}`}>{q.name}</button>;
                })}
                {quals.length === 0 && <span className="text-[11px] text-slate-400">No qualifications defined</span>}
              </div>
            </div>
            <div>
              <Label className="text-xs">Functional Positions (1 or more)</Label>
              {eligEmp && <p className="text-[10px] text-slate-400 mt-0.5">Highest qualification: <b className="text-slate-600">{eligEmp.max_qualification_name || 'None'}</b> · Experience: {eligEmp.experience_years || 0} yr</p>}
              <div className="flex flex-wrap gap-1.5 mt-1">
                {fps.map(fp => {
                  const active = (emp.functional_position_ids || []).includes(fp.id);
                  const r = elig[fp.id];
                  const showDot = r && !r.no_requirement;
                  return <button key={fp.id} disabled={!canManage} onClick={() => toggleFp(fp)}
                    className={`text-[11px] px-2 py-1 rounded border flex items-center gap-1 ${active ? 'bg-pink-50 text-pink-700 border-pink-300' : 'bg-white text-slate-500 border-slate-200'}`}
                    data-testid={`hr-emp-fp-${fp.id}`}>
                    {showDot && <span className={`w-1.5 h-1.5 rounded-full ${r.eligible ? 'bg-emerald-500' : 'bg-red-500'}`} />}
                    {fp.name}</button>;
                })}
                {fps.length === 0 && <span className="text-[11px] text-slate-400">No functional positions defined</span>}
              </div>
              {(emp.functional_position_ids || []).length > 0 && (
                <div className="mt-2 space-y-1" data-testid="hr-emp-eligibility">
                  {(emp.functional_position_ids || []).map(fpId => {
                    const r = elig[fpId]; if (!r) return null;
                    return (
                      <div key={fpId} className="flex items-center gap-2 text-[11px]">
                        <Badge variant="outline" className={`text-[9px] ${r.no_requirement ? 'bg-slate-50 text-slate-500 border-slate-200' : r.eligible ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                          {r.no_requirement ? 'No req.' : r.eligible ? 'Eligible' : 'Not eligible'}
                        </Badge>
                        <span className="text-slate-500">{r.fp_name}</span>
                        <span className="text-slate-400">— {reasonText(r)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {canManage && <Button size="sm" onClick={saveProfile} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white gap-1" data-testid="hr-emp-save-profile"><Save className="w-3.5 h-3.5" /> Save Profile</Button>}
          </CardContent>
        </Card>

        {/* Attachments */}
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-base">Documents</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {canManage && (
              <div className="flex items-center gap-2">
                <Select value={attCat} onValueChange={setAttCat}>
                  <SelectTrigger className="h-8 text-xs w-40" data-testid="hr-emp-att-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="qualification">Qualification (Degree/Diploma)</SelectItem>
                    <SelectItem value="experience">Work Experience Proof</SelectItem>
                  </SelectContent>
                </Select>
                <input type="file" ref={attRef} onChange={onAttachment} className="hidden" data-testid="hr-emp-att-input" />
                <Button size="sm" variant="outline" onClick={() => attRef.current?.click()} className="gap-1" data-testid="hr-emp-att-upload"><Upload className="w-3.5 h-3.5" /> Upload</Button>
              </div>
            )}
            <div className="space-y-1.5">
              {(emp.attachments || []).length === 0 ? <p className="text-[11px] text-slate-400">No documents uploaded</p> :
                (emp.attachments || []).map(a => (
                  <div key={a.id} className="flex items-center justify-between border rounded px-2 py-1.5" data-testid={`hr-emp-att-${a.id}`}>
                    <a href={`${BACKEND}/api/hr/attachments/${id}/${a.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-blue-600 hover:underline truncate">
                      <FileText className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{a.filename}</span>
                      <Badge variant="outline" className="text-[9px] shrink-0">{a.category}</Badge>
                    </a>
                    {canManage && <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => delAttachment(a.id)}><Trash2 className="w-3 h-3" /></Button>}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Skill Levels */}
      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base">Current Skill Levels</CardTitle>
          {canManage && <Button size="sm" onClick={saveLevels} disabled={saving} className="bg-pink-600 hover:bg-pink-700 text-white gap-1" data-testid="hr-emp-save-levels"><Save className="w-3.5 h-3.5" /> Save Levels</Button>}
        </CardHeader>
        <CardContent>
          {gaps.length > 0 && (
            <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-start gap-1.5" data-testid="hr-emp-gaps">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{gaps.length} skill(s) below the level required by this employee's functional position(s). A training will be planned (Phase 4).</span>
            </div>
          )}

          {canManage && (
            <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-dashed border-slate-300 p-2" data-testid="hr-emp-add-skill">
              <span className="text-[11px] font-medium text-slate-500 w-full">Add another skill to rate</span>
              <div>
                <Label className="text-[10px] text-slate-400">Skill Group</Label>
                <Select value={addGroup || undefined} onValueChange={v => { setAddGroup(v); setAddSkill(''); }}>
                  <SelectTrigger className="h-8 w-48 text-xs" data-testid="hr-emp-add-group"><SelectValue placeholder="Select group" /></SelectTrigger>
                  <SelectContent>{groupSkills(skills).map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Skill</Label>
                <div className="w-52">
                  <SearchableSelect
                    options={addableSkills.map(s => ({ value: s.id, label: s.name, sub: s.skill_type }))}
                    value={addSkill} onSelect={setAddSkill} disabled={!addGroup}
                    placeholder={addGroup ? 'Search skill…' : 'Pick a group first'} testId="hr-emp-add-skill-select"
                  />
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-8 gap-1" onClick={addRelevantSkill} data-testid="hr-emp-add-skill-btn"><Plus className="w-3.5 h-3.5" /> Add</Button>
            </div>
          )}

          {relevantGroups.length === 0 ? (
            <p className="text-[11px] text-slate-400">No relevant skills yet. Assign a functional position or add a skill above.</p>
          ) : (
            <div className="space-y-4">
              {relevantGroups.map(g => (
                <div key={g.group_id} data-testid={`hr-emp-skillgroup-${g.group_id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">{g.group_name}</p>
                    {canManage && (
                      <div className="flex items-center gap-1" data-testid={`hr-emp-bulk-${g.group_id}`}>
                        <span className="text-[10px] text-slate-400">Set all:</span>
                        {LEVELS.map(l => <button key={l} type="button" onClick={() => setGroupLevel(g.skills, l)} className="text-[10px] px-1.5 py-0.5 rounded border bg-white text-slate-500 border-slate-200 hover:bg-pink-50 hover:text-pink-700 hover:border-pink-300" data-testid={`hr-emp-bulk-${g.group_id}-${l}`}>{l}</button>)}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {g.skills.map(s => {
                      const req = reqMap[s.id]; const cur = levelEdits[s.id];
                      const deficient = !!req && (!cur || LEVELS.indexOf(cur) < LEVELS.indexOf(req));
                      return (
                        <div key={s.id} className={`flex items-center justify-between gap-2 border rounded px-2.5 py-1.5 ${deficient ? 'border-amber-300 bg-amber-50/40' : ''}`} data-testid={`hr-emp-skill-${s.id}`}>
                          <div className="min-w-0">
                            <p className="text-xs font-medium truncate">{s.name}</p>
                            <p className="text-[10px] text-slate-400">{s.skill_type}{req ? ` · required ${req}` : ''}</p>
                          </div>
                          <Select value={cur || '__none__'} onValueChange={v => setLevelEdits(p => ({ ...p, [s.id]: v === '__none__' ? '' : v }))} disabled={!canManage}>
                            <SelectTrigger className="h-7 w-24 text-xs shrink-0" data-testid={`hr-emp-level-${s.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">—</SelectItem>
                              {LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Direct reports */}
      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users2 className="w-4 h-4 text-slate-500" /> Direct Reports ({directReports.length})</CardTitle></CardHeader>
        <CardContent>
          {directReports.length === 0 ? <p className="text-[11px] text-slate-400">No one reports to this employee</p> :
            <div className="flex flex-wrap gap-2">
              {directReports.map(r => <button key={r.id} onClick={() => navigate(`/hr/employees/${r.id}`)} className="text-xs px-2.5 py-1.5 rounded border bg-white hover:bg-slate-50" data-testid={`hr-emp-report-${r.id}`}>{r.name}</button>)}
            </div>}
        </CardContent>
      </Card>
    </div>
  );
}
